import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API_URL, getAuthHeaders, getUserRoles, getPaymentTypes, getLocale } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { useApiMessages } from "../hooks/useApiMessages";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "../components/ui/pagination";
import { Users as UsersIcon, Plus, Pencil, Trash2, Search, Shield, KeyRound, X } from "lucide-react";
import { toast } from "sonner";

export const Users = () => {
  const { t, i18n } = useTranslation();
  const { getErrorMessage, translateMessage } = useApiMessages();
  const { user: currentUser, isAdmin, isRevendedor, canManageCompanies } = useAuth();
  
  // Recalcular locale cuando cambie el idioma
  const currentLocale = useMemo(() => getLocale(), [i18n.language]);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [temporaryPassword, setTemporaryPassword] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: "vigilante",
    company_id: "",
    hourly_rate: 0,
    fixed_salary: 0,
    payment_type: "hourly",
    pay_lunch_hours: false,
    company_limit: null,
  });

  const fetchData = async () => {
    try {
      // Construir URL con filtro de compañía si es necesario
      let usersUrl = `${API_URL}/users`;
      if ((isAdmin() || isRevendedor()) && selectedCompanyId !== "all") {
        usersUrl += `?company_id=${selectedCompanyId}`;
      }
      
      const [usersRes, companiesRes] = await Promise.all([
        axios.get(usersUrl, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/companies`, { headers: getAuthHeaders() }),
      ]);
      const usersData = Array.isArray(usersRes.data) ? usersRes.data : [];
      console.log("Fetched users:", usersData);
      const revendedorUsers = usersData.filter(u => u.role === "revendedor");
      console.log("Revendedor users:", revendedorUsers);
      revendedorUsers.forEach(u => {
        console.log(`User ${u.name} (${u.id}): company_limit =`, u.company_limit, "type:", typeof u.company_limit);
      });
      setUsers(usersData);
      setCompanies(Array.isArray(companiesRes.data) ? companiesRes.data : []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(t("pages.users.errorLoadingData"));
      setUsers([]);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedCompanyId]);

  const handleOpenForm = (user = null) => {
    if (user) {
      console.log("Opening form for user:", user);
      console.log("user.company_limit:", user.company_limit, "type:", typeof user.company_limit);
      setSelectedUser(user);
      
      // Asegurar que company_limit sea un número o null
      let companyLimitValue = null;
      if (user.company_limit !== undefined && user.company_limit !== null && user.company_limit !== "") {
        const parsed = typeof user.company_limit === "string" ? parseInt(user.company_limit, 10) : Number(user.company_limit);
        companyLimitValue = !isNaN(parsed) && parsed > 0 ? parsed : null;
      }
      console.log("Setting company_limit to:", companyLimitValue, "type:", typeof companyLimitValue);
      
      setFormData({
        email: user.email,
        password: "",
        name: user.name,
        role: user.role,
        company_id: user.company_id,
        hourly_rate: user.hourly_rate || 0,
        fixed_salary: user.fixed_salary || 0,
        payment_type: user.payment_type || "hourly",
        pay_lunch_hours: user.pay_lunch_hours || false,
        company_limit: companyLimitValue,
      });
      console.log("FormData set, company_limit:", companyLimitValue);
    } else {
      setSelectedUser(null);
      setFormData({
        email: "",
        password: "",
        name: "",
        role: "vigilante",
        company_id: canManageCompanies() ? "" : currentUser.company_id,
        hourly_rate: 0,
        fixed_salary: 0,
        payment_type: "hourly",
        pay_lunch_hours: false,
        company_limit: null,
      });
    }
    setIsFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedUser) {
        const updateData = { ...formData };
        delete updateData.password;
        delete updateData.email;
        // Solo incluir company_limit si el rol es revendedor
        if (updateData.role !== "revendedor") {
          delete updateData.company_limit;
        } else {
          // Si es revendedor, asegurar que company_limit se envíe (incluso si es null)
          if (updateData.company_limit === "" || updateData.company_limit === undefined) {
            updateData.company_limit = null;
          }
        }
        console.log("Sending update request with data:", updateData);
        const response = await axios.put(`${API_URL}/users/${selectedUser.id}`, updateData, {
          headers: getAuthHeaders(),
        });
        console.log("Update response:", response.data);
        toast.success(t("api.success.userUpdated"));
      } else {
        const createData = { ...formData };
        // Solo incluir company_limit si el rol es revendedor
        if (createData.role !== "revendedor") {
          delete createData.company_limit;
        } else {
          // Si es revendedor, asegurar que company_limit se envíe (incluso si es null)
          if (createData.company_limit === "" || createData.company_limit === undefined) {
            createData.company_limit = null;
          }
        }
        await axios.post(`${API_URL}/users`, createData, {
          headers: getAuthHeaders(),
        });
        toast.success(t("api.success.userCreated"));
      }
      setIsFormOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "api.errors.saveError"));
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/users/${selectedUser.id}`, {
        headers: getAuthHeaders(),
      });
      toast.success(t("api.success.userDeleted"));
      setIsDeleteOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "api.errors.deleteError"));
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser) return;
    
    setResettingPassword(true);
    try {
      const response = await axios.post(
        `${API_URL}/users/${resetPasswordUser.id}/reset-password`,
        {},
        { headers: getAuthHeaders() }
      );
      
      console.log("Reset password response:", response.data);
      
      // El backend siempre devuelve temporary_password (puede ser null si no es temporal)
      const tempPassword = response.data?.temporary_password;
      console.log("Temporary password received:", tempPassword);
      
      if (tempPassword) {
        setTemporaryPassword(tempPassword);
        // Asegurar que el diálogo permanezca abierto
        setIsResetPasswordOpen(true);
        toast.success(t("api.success.passwordReset"));
      } else {
        // Si no hay contraseña temporal, cerrar el diálogo
        toast.success(t("api.success.passwordReset"));
        setIsResetPasswordOpen(false);
        setResetPasswordUser(null);
        setTemporaryPassword(null);
      }
    } catch (error) {
      console.error("Error al resetear contraseña:", error);
      toast.error(getErrorMessage(error, "api.errors.unknownError"));
    } finally {
      setResettingPassword(false);
    }
  };

  const filteredUsers = Array.isArray(users) ? users.filter(
    (u) =>
      u?.name?.toLowerCase().includes(search.toLowerCase()) ||
      u?.email?.toLowerCase().includes(search.toLowerCase())
  ) : [];

  // Paginación
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // Resetear a página 1 cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [search, selectedCompanyId]);

  const getRoleBadge = (role) => {
    const styles = {
      admin: "bg-purple-100 text-purple-700",
      revendedor: "bg-orange-100 text-orange-700",
      supervisor: "bg-blue-100 text-blue-700",
      vigilante: "bg-green-100 text-green-700",
    };
    const labels = {
      admin: t("roles.admin"),
      revendedor: t("roles.revendedor"),
      supervisor: t("roles.supervisor"),
      vigilante: t("roles.vigilante"),
    };
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${styles[role]}`}>
        {labels[role]}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="users-page" key={i18n.language}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">{t("pages.users.title")}</h1>
          <p className="text-slate-600 mt-1">{t("pages.users.subtitle")}</p>
        </div>
        <Button onClick={() => handleOpenForm()} data-testid="add-user-btn">
          <Plus className="h-4 w-4 mr-2" />
          {t("pages.users.newUser")}
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            {/* Search Bar */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={t("pages.users.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`pl-10 ${search ? "pr-10" : ""}`}
                data-testid="search-users"
              />
              {search && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 hover:bg-slate-100"
                  onClick={() => setSearch("")}
                  title={t("pages.users.clearSearch")}
                >
                  <X className="h-4 w-4 text-slate-400" />
                </Button>
              )}
            </div>
            
            {/* Company Filter - Solo para Admin y Revendedor */}
            {(isAdmin() || isRevendedor()) && (
              <div className="w-full sm:w-[250px]">
                <Label className="text-sm text-slate-600 mb-1 block">{t("pages.users.filterByCompany")}</Label>
                <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("pages.users.allCompanies")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("pages.users.allCompanies")}</SelectItem>
                    {Array.isArray(companies) && companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Items per page selector */}
            <div className="w-full sm:w-auto">
              <Label className="text-sm text-slate-600 mb-1 block">{t("pages.users.perPage")}</Label>
              <Select
                value={itemsPerPage.toString()}
                onValueChange={(value) => {
                  setItemsPerPage(parseInt(value));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-3">
            {t("pages.users.usersFound", { count: filteredUsers.length })}
          </p>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("pages.users.user")}</TableHead>
                <TableHead>{t("pages.users.role")}</TableHead>
                <TableHead>{t("pages.users.paymentType")}</TableHead>
                <TableHead>{t("pages.users.hourlyRate")}</TableHead>
                <TableHead>{t("pages.users.fixedSalary")}</TableHead>
                <TableHead>{t("pages.users.status")}</TableHead>
                <TableHead className="text-right">{t("pages.users.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : paginatedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    {t("pages.users.noUsers")}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                          <span className="text-sm font-bold text-slate-600">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-sm text-slate-500">{user.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell className="text-slate-600 capitalize">
                      {user.payment_type === "hourly" ? t("pages.users.hourly") : t("pages.users.mixed")}
                    </TableCell>
                    <TableCell className="font-mono">
                      ${user.hourly_rate?.toFixed(2) || "0.00"}
                    </TableCell>
                    <TableCell className="font-mono">
                      ${user.fixed_salary ? user.fixed_salary.toLocaleString(currentLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00"}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          user.is_active
                            ? "bg-green-100 text-green-700"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {user.is_active ? t("pages.users.active") : t("pages.users.inactive")}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenForm(user)}
                          data-testid={`edit-user-${user.id}`}
                          title={t("pages.users.editUser")}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => {
                            setResetPasswordUser(user);
                            setTemporaryPassword(null);
                            setIsResetPasswordOpen(true);
                          }}
                          data-testid={`reset-password-user-${user.id}`}
                          title={t("pages.users.resetPassword")}
                        >
                          <KeyRound className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setSelectedUser(user);
                            setIsDeleteOpen(true);
                          }}
                          data-testid={`delete-user-${user.id}`}
                          title={t("pages.users.deleteUser")}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
        {totalPages > 1 && (
          <div className="border-t p-4">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Mostrar primera página, última página, página actual y páginas adyacentes
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <PaginationItem key={page}>
                        <PaginationLink
                          onClick={() => setCurrentPage(page)}
                          isActive={currentPage === page}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return (
                      <PaginationItem key={page}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    );
                  }
                  return null;
                })}
                
                <PaginationItem>
                  <PaginationNext
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
            <div className="text-center text-sm text-slate-500 mt-2">
              {t("pages.users.showingUsers", { 
                start: startIndex + 1, 
                end: Math.min(endIndex, filteredUsers.length), 
                total: filteredUsers.length 
              })}
            </div>
          </div>
        )}
      </Card>

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedUser ? t("pages.users.editUserTitle") : t("pages.users.newUserTitle")}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t("pages.users.name")} *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="user-name-input"
              />
            </div>
            
            {!selectedUser && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email">{t("common.email")} *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="user-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">{t("common.password")} *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="user-password-input"
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>{t("pages.users.role")} *</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Supervisor y Revendedor no pueden asignar roles de admin o revendedor */}
                  {getUserRoles(t).filter((role) => 
                    isAdmin() ? true : !["admin", "revendedor"].includes(role.value)
                  ).map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Admin y Revendedor ven selector de compañía, supervisor usa su compañía automáticamente */}
            {canManageCompanies() ? (
              <div className="space-y-2">
                <Label>{t("pages.companies.name")} *</Label>
                <Select
                  value={formData.company_id}
                  onValueChange={(value) => setFormData({ ...formData, company_id: value })}
                >
                  <SelectTrigger data-testid="user-company-select">
                    <SelectValue placeholder={t("pages.companies.selectCompany")} />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.isArray(companies) && companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            {/* Campo para límite de compañías (solo para revendedores) */}
            {formData.role === "revendedor" && isAdmin() && (
              <div className="space-y-2">
                <Label htmlFor="company_limit">{t("pages.users.companyLimit")}</Label>
                <Input
                  id="company_limit"
                  type="number"
                  min="1"
                  step="1"
                  value={formData.company_limit !== null && formData.company_limit !== undefined && formData.company_limit !== "" ? String(formData.company_limit) : ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    console.log("Input onChange - value:", value, "type:", typeof value);
                    setFormData({
                      ...formData,
                      company_limit: value && value.trim() !== "" ? parseInt(value, 10) : null,
                    });
                  }}
                  placeholder={t("pages.users.noLimit")}
                  data-testid="user-company-limit-input"
                />
                <p className="text-xs text-slate-500">
                  {t("pages.users.companyLimitDescription")}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>{t("pages.users.paymentType")}</Label>
              <Select
                value={formData.payment_type}
                onValueChange={(value) => {
                  // Si cambia a "hourly", limpiar fixed_salary
                  // Si cambia a "mixed", mantener ambos valores
                  setFormData({
                    ...formData,
                    payment_type: value,
                    fixed_salary: value === "hourly" ? 0 : formData.fixed_salary,
                  });
                }}
              >
                <SelectTrigger data-testid="user-payment-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getPaymentTypes(t).map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Campos de pago - mostrar según el tipo seleccionado */}
            {formData.payment_type === "hourly" ? (
              // Solo mostrar tarifa/hora si es "Por hora"
              <div className="space-y-2">
                <Label htmlFor="hourly_rate">{t("pages.users.hourlyRateRequired")}</Label>
                <Input
                  id="hourly_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.hourly_rate}
                  onChange={(e) =>
                    setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 })
                  }
                  data-testid="user-hourly-rate-input"
                />
              </div>
            ) : (
              // Mostrar ambos campos si es "Mixto"
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="hourly_rate">{t("pages.users.hourlyRateLabel")}</Label>
                  <Input
                    id="hourly_rate"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.hourly_rate}
                    onChange={(e) =>
                      setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 })
                    }
                    data-testid="user-hourly-rate-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fixed_salary">{t("pages.users.fixedSalaryLabel")}</Label>
                  <Input
                    id="fixed_salary"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.fixed_salary}
                    onChange={(e) =>
                      setFormData({ ...formData, fixed_salary: parseFloat(e.target.value) || 0 })
                    }
                    data-testid="user-fixed-salary-input"
                  />
                </div>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="pay_lunch_hours"
                checked={formData.pay_lunch_hours}
                onChange={(e) => setFormData({ ...formData, pay_lunch_hours: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                data-testid="user-pay-lunch-checkbox"
              />
              <Label htmlFor="pay_lunch_hours" className="text-sm font-medium cursor-pointer">
                {t("pages.users.payLunchHours")}
              </Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" data-testid="save-user-btn">
                {selectedUser ? t("common.saveChanges") : t("pages.users.createUser")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <AlertDialog open={isResetPasswordOpen} onOpenChange={(open) => {
        setIsResetPasswordOpen(open);
        if (!open) {
          setResetPasswordUser(null);
          setTemporaryPassword(null);
        }
      }}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("pages.users.resetPassword")}</AlertDialogTitle>
            <AlertDialogDescription>
              {temporaryPassword ? (
                <div className="space-y-4">
                  <p>
                    {t("pages.users.passwordResetSuccess", { name: resetPasswordUser?.name })}
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-blue-900 mb-2">
                      {t("pages.users.temporaryPassword")}
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-white px-3 py-2 rounded border border-blue-300 font-mono text-lg font-bold text-blue-700 break-all select-all">
                        {temporaryPassword || t("common.notAvailable")}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(temporaryPassword);
                          toast.success(t("api.success.passwordCopied"));
                        }}
                      >
                        {t("pages.users.copy")}
                      </Button>
                    </div>
                    <p className="text-xs text-blue-700 mt-2">
                      {t("pages.users.sharePasswordMessage")}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {t("pages.users.confirmResetPassword", { name: resetPasswordUser?.name })}
                  <p className="text-sm text-slate-500 mt-2">
                    {t("pages.users.temporaryPasswordInfo")}
                  </p>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {temporaryPassword ? (
              <AlertDialogAction
                onClick={() => {
                  setIsResetPasswordOpen(false);
                  setResetPasswordUser(null);
                  setTemporaryPassword(null);
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {t("pages.users.close")}
              </AlertDialogAction>
            ) : (
              <>
                <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleResetPassword}
                  disabled={resettingPassword}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="confirm-reset-password"
                >
                  {resettingPassword ? t("pages.users.resetting") : t("pages.users.resetPassword")}
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("pages.users.deleteUserConfirm")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("pages.users.deleteUserWarning", { name: selectedUser?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-user"
            >
              {t("pages.users.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
